import { useEffect, useMemo, useState } from 'react';
import { Link } from '@/lib/router-compat';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Target,
  Radio,
  Bookmark,
  History,
  Calendar,
  Sparkles,
  Star,
  Wallet,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { oddpoolService, type OddpoolEvent } from '@/services/oddpoolService';
import { polymarketEventService } from '@/services/polymarketEventService';
import DailyDigestCard from '@/components/prediction-markets/DailyDigestCard';
import LiveFeedsViewer from '@/components/prediction-markets/LiveFeedsViewer';
import HistoricalViewer from '@/components/prediction-markets/HistoricalViewer';
import ResearchCopilot from '@/components/prediction-markets/ResearchCopilot';
import VenueAccountPanel from '@/components/prediction-markets/VenueAccountPanel';
import VenueBadge from '@/components/prediction-markets/VenueBadge';
import { EventCard } from '@/components/polymarket/EventCard';
import { PageShell } from '@/components/layout/PageShell';
import { PageHeader } from '@/components/ui/page-header';
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

const CATEGORIES = [
  { label: 'Trending', query: '' },
  { label: 'Bitcoin', query: 'bitcoin' },
  { label: 'Ethereum', query: 'ethereum' },
  { label: 'Crypto', query: 'crypto' },
  { label: 'Economics', query: 'fed' },
  { label: 'Politics', query: 'election' },
  { label: 'AI & Tech', query: 'ai' },
];

type SortKey = 'volume' | 'liquidity' | 'markets' | 'newest';

function formatVolume(v?: number) {
  if (v == null) return '-';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

function formatClose(iso?: string) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  if (diff <= 0) return 'Closed';
  const days = Math.floor(diff / 86_400_000);
  if (days >= 1) return `${days}d left`;
  const hours = Math.floor(diff / 3_600_000);
  if (hours >= 1) return `${hours}h left`;
  return `${Math.max(1, Math.floor(diff / 60_000))}m left`;
}

function useDebounced<T>(value: T, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function EventRow({
  event,
  isTracked,
  onTrack,
}: {
  event: OddpoolEvent;
  isTracked: boolean;
  onTrack: () => void;
}) {
  const closes = formatClose(event.end_date);
  const initials = event.title.slice(0, 2).toUpperCase();

  return (
    <div className="flex items-start gap-4 px-6 py-4 hover:bg-muted/40 transition-colors">
      <Link
        to={`/prediction-markets/${encodeURIComponent(event.event_id)}`}
        className="shrink-0"
        aria-hidden="true"
        tabIndex={-1}
      >
        {event.image_url ? (
          <img
            src={event.image_url}
            alt={`${event.title} - prediction market event`}
            loading="lazy"
            className="h-11 w-11 rounded-lg object-cover border border-border bg-muted"
          />
        ) : (
          <div className="h-11 w-11 rounded-lg border border-border bg-muted flex items-center justify-center text-[11px] font-bold text-muted-foreground">
            {initials}
          </div>
        )}
      </Link>

      <div className="min-w-0 flex-1">
        <Link
          to={`/prediction-markets/${encodeURIComponent(event.event_id)}`}
          className="text-sm font-semibold text-foreground line-clamp-2 hover:underline"
        >
          {event.title}
        </Link>
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <VenueBadge venue={event.venue} />
          {event.category && (
            <span className="px-2 py-0.5 rounded-full bg-muted text-[11px] font-medium text-muted-foreground">
              {event.category}
            </span>
          )}
          {event.market_count != null && (
            <span className="text-[11px] text-muted-foreground">
              {event.market_count} market{event.market_count === 1 ? '' : 's'}
            </span>
          )}
          {closes && (
            <span className="text-[11px] text-muted-foreground">{closes}</span>
          )}
        </div>
        {event.market_questions?.length ? (
          <p className="mt-1.5 text-xs text-muted-foreground line-clamp-1">
            {event.market_questions.slice(0, 3).join(' · ')}
          </p>
        ) : null}
      </div>

      <div className="shrink-0 text-right w-28">
        <p className="text-sm font-bold tabular-nums text-foreground">
          {formatVolume(event.volume)}
        </p>
        <p className="text-[11px] text-muted-foreground tabular-nums">
          {formatVolume(event.liquidity)} liq
        </p>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="shrink-0"
        aria-label={isTracked ? 'Untrack event' : 'Track event'}
        onClick={onTrack}
      >
        <Star
          className={`h-4 w-4 ${isTracked ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground'}`}
        />
      </Button>
    </div>
  );
}

export default function PredictionMarkets() {
  const t = useT();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('markets');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Trending');
  const [sort, setSort] = useState<SortKey>('volume');
  const [showEmpty, setShowEmpty] = useState(false);
  const debouncedSearch = useDebounced(search.trim(), 450);

  const activeQuery =
    debouncedSearch || CATEGORIES.find((c) => c.label === category)?.query || '';

  const {
    data: eventsData,
    isLoading: eventsLoading,
    isFetching: eventsFetching,
    error: eventsError,
  } = useQuery({
    queryKey: ['oddpool-events', activeQuery],
    queryFn: () =>
      activeQuery
        ? oddpoolService.searchEvents(activeQuery, 60)
        : oddpoolService.browseEvents(25),
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 180_000,
  });

  const allEvents: OddpoolEvent[] = useMemo(
    () => eventsData?.events ?? [],
    [eventsData]
  );

  const events = useMemo(() => {
    const filtered = showEmpty
      ? allEvents
      : allEvents.filter((e) => (e.volume ?? 0) > 0 || (e.liquidity ?? 0) > 0);
    const list = filtered.length ? filtered : allEvents;
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sort) {
        case 'liquidity':
          return (b.liquidity ?? 0) - (a.liquidity ?? 0);
        case 'markets':
          return (b.market_count ?? 0) - (a.market_count ?? 0);
        case 'newest':
          return String(b.discovered_at ?? '').localeCompare(String(a.discovered_at ?? ''));
        default:
          return (b.volume ?? 0) - (a.volume ?? 0);
      }
    });
    return sorted;
  }, [allEvents, showEmpty, sort]);

  const hiddenCount = allEvents.length - events.length;

  const { data: trackedIds = [] } = useQuery({
    queryKey: ['oddpool-tracked', user?.id],
    queryFn: () => (user ? oddpoolService.getTrackedEventIds(user.id) : Promise.resolve([])),
    enabled: !!user,
  });

  const trackMutation = useMutation({
    mutationFn: async (e: OddpoolEvent) => {
      if (!user) throw new Error('Sign in to track markets');
      if (trackedIds.includes(e.event_id)) {
        await oddpoolService.untrackEvent(user.id, e.event_id);
        return { tracked: false };
      }
      await oddpoolService.trackEvent(user.id, e.event_id, e.venue, e.title);
      return { tracked: true };
    },
    onSuccess: ({ tracked }) => {
      toast.success(tracked ? 'Tracking event' : 'Untracked');
      queryClient.invalidateQueries({ queryKey: ['oddpool-tracked'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const trackedEvents = useMemo(
    () => allEvents.filter((e) => trackedIds.includes(e.event_id)),
    [allEvents, trackedIds]
  );

  const { data: bookmarkedEvents = [], isLoading: bookmarksLoading } = useQuery({
    queryKey: ['polymarket-bookmarked-events', user?.id],
    queryFn: () => (user ? polymarketEventService.getBookmarkedEvents(user.id) : []),
    enabled: !!user && tab === 'bookmarks',
  });
  const { data: bookmarkedEventIds = [] } = useQuery({
    queryKey: ['polymarket-bookmarks', user?.id],
    queryFn: () => (user ? polymarketEventService.getUserBookmarks(user.id) : []),
    enabled: !!user,
  });

  const totalVolume = useMemo(
    () => events.reduce((sum, e) => sum + (e.volume ?? 0), 0),
    [events]
  );
  const totalLiquidity = useMemo(
    () => events.reduce((sum, e) => sum + (e.liquidity ?? 0), 0),
    [events]
  );

  return (
    <PageShell>
      <PageHeader
        eyebrow={t("auto.prediction-markets-b485")}
        title={t("auto.market-intelligence-abf2")}
        description={t("auto.unified-view-of-global-event-markets-liv-8642")}
        actions={
          <div className="h-10 px-4 bg-card border border-border rounded-lg flex items-center gap-3 shadow-xs">
            <div
              className={`w-2 h-2 rounded-full ${eventsFetching ? 'bg-amber-500' : 'bg-success'} animate-pulse`}
            />
            <span className="text-xs font-medium text-muted-foreground">
              {eventsFetching ? 'Syncing…' : 'Live market feed'}
            </span>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-6">
        <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center justify-between">
          <TabsList className="h-auto p-1 bg-muted/60 rounded-xl flex-wrap justify-start">
            <TabsTrigger value="markets" className="gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-xs rounded-lg"><Target className="h-3.5 w-3.5" /> <T k="auto.markets-c3c4" /></TabsTrigger>
            <TabsTrigger value="digest" className="gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-xs rounded-lg"><Calendar className="h-3.5 w-3.5" /> <T k="auto.digest-47fd" /></TabsTrigger>
            <TabsTrigger value="live" className="gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-xs rounded-lg"><Radio className="h-3.5 w-3.5" /> <T k="auto.live-65c8" /></TabsTrigger>
            <TabsTrigger value="historical" className="gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-xs rounded-lg"><History className="h-3.5 w-3.5" /> <T k="auto.historical-bb2f" /></TabsTrigger>
            <TabsTrigger value="bookmarks" className="gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-xs rounded-lg"><Bookmark className="h-3.5 w-3.5" /> <T k="auto.bookmarks-ca3c" /></TabsTrigger>
            <TabsTrigger value="account" className="gap-1.5 data-[state=active]:bg-card data-[state=active]:shadow-xs rounded-lg"><Wallet className="h-3.5 w-3.5" /> <T k="auto.account-85df" /></TabsTrigger>
          </TabsList>

          {tab === 'markets' && (
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("auto.search-events-e-g-bitcoin-fed-election-eca7")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-card rounded-xl shadow-xs"
              />
            </div>
          )}
        </div>

        <TabsContent value="markets" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Events listed', value: events.length.toString() },
                  { label: 'Combined volume', value: formatVolume(totalVolume) },
                  { label: 'Liquidity', value: formatVolume(totalLiquidity) },
                  { label: 'Tracked by you', value: trackedIds.length.toString() },
                ].map((s) => (
                  <div key={s.label} className="bg-card border border-border rounded-xl p-4 shadow-xs">
                    <p className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{s.label}</p>
                    <p className="text-xl font-bold text-foreground mt-1 tabular-nums">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.label}
                    type="button"
                    onClick={() => {
                      setCategory(c.label);
                      setSearch('');
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      !debouncedSearch && category === c.label
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-muted-foreground border-border hover:bg-muted'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
                <div className="ml-auto">
                  <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
                    <SelectTrigger className="h-8 w-[150px] text-xs bg-card">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="volume"><T k="auto.sort-volume-b0aa" /></SelectItem>
                      <SelectItem value="liquidity"><T k="auto.sort-liquidity-d93a" /></SelectItem>
                      <SelectItem value="markets"><T k="auto.sort-markets-5083" /></SelectItem>
                      <SelectItem value="newest"><T k="auto.sort-newest-682d" /></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-xs">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/40">
                  <h2 className="text-sm font-bold text-foreground uppercase tracking-tight">
                    {debouncedSearch ? `Results for “${debouncedSearch}”` : `${category} events`}
                  </h2>
                  <span className="text-[11px] text-muted-foreground font-medium">{events.length} listed</span>
                </div>

                {eventsLoading ? (
                  <div className="p-6 space-y-3">
                    {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                  </div>
                ) : eventsError ? (
                  <div className="py-12 text-center space-y-3">
                    <p className="text-sm text-muted-foreground">{(eventsError as Error).message}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => queryClient.invalidateQueries({ queryKey: ['oddpool-events'] })}
                    >
                      <T k="auto.retry-6327" />
                    </Button>
                  </div>
                ) : events.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    No events found{debouncedSearch ? ` for “${debouncedSearch}”` : ''}. Try another topic.
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {events.slice(0, 40).map((e) => (
                      <EventRow
                        key={`${e.venue}-${e.event_id}`}
                        event={e}
                        isTracked={trackedIds.includes(e.event_id)}
                        onTrack={() =>
                          user ? trackMutation.mutate(e) : toast.error('Sign in to track')
                        }
                      />
                    ))}
                  </div>
                )}

                {!eventsLoading && !eventsError && hiddenCount > 0 && (
                  <div className="px-6 py-3 border-t border-border bg-muted/30 text-center">
                    <button
                      type="button"
                      onClick={() => setShowEmpty((s) => !s)}
                      className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                    >
                      {showEmpty
                        ? 'Hide zero-volume events'
                        : `Show ${hiddenCount} zero-volume event${hiddenCount === 1 ? '' : 's'}`}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-4 space-y-6">
              <div className="bg-card border border-border rounded-2xl shadow-xs overflow-hidden">
                <div className="px-5 py-4 border-b border-border">
                  <h2 className="text-sm font-bold text-foreground"><T k="auto.your-watchlist-b916" /></h2>
                </div>
                <div className="p-5 space-y-3 max-h-[420px] overflow-y-auto">
                  {!user ? (
                    <p className="text-xs text-muted-foreground"><T k="auto.sign-in-to-track-events-and-build-a-watc-50f5" /></p>
                  ) : trackedIds.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      <T k="auto.nothing-tracked-yet-use-the-star-icon-on-723c" />
                    </p>
                  ) : trackedEvents.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {trackedIds.length} tracked event{trackedIds.length === 1 ? '' : 's'}, none in the current view.
                    </p>
                  ) : (
                    trackedEvents.map((e) => (
                      <Link
                        key={e.event_id}
                        to={`/prediction-markets/${encodeURIComponent(e.event_id)}`}
                        className="block p-3 rounded-lg border border-border hover:bg-muted/40 transition-colors"
                      >
                        <p className="text-sm font-medium line-clamp-2">{e.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 font-mono">
                          {formatVolume(e.volume)} vol · {e.market_count ?? 0} markets
                        </p>
                      </Link>
                    ))
                  )}
                </div>
              </div>

              <div className="bg-primary rounded-2xl p-6 text-primary-foreground shadow-xl relative overflow-hidden">
                <div className="absolute -right-8 -bottom-8 w-32 h-32 bg-primary-foreground/20 rounded-full blur-3xl" />
                <div className="relative z-10 space-y-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    <h3 className="font-bold tracking-tight"><T k="auto.ai-market-copilot-3694" /></h3>
                  </div>
                  <p className="text-sm opacity-90 leading-relaxed">
                    <T k="auto.cross-venue-analysis-and-market-research-18fe" />
                  </p>
                  <Button
                    variant="secondary"
                    className="w-full font-bold"
                    onClick={() => {
                      const btn = document.querySelector<HTMLButtonElement>('[data-research-copilot-trigger]');
                      btn?.click();
                    }}
                  >
                    <T k="auto.open-copilot-d576" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="digest" className="mt-0">
          <DailyDigestCard />
        </TabsContent>

        <TabsContent value="live" className="mt-0">
          <LiveFeedsViewer />
        </TabsContent>

        <TabsContent value="historical" className="mt-0">
          <HistoricalViewer />
        </TabsContent>

        <TabsContent value="bookmarks" className="mt-0 space-y-3">
          {bookmarksLoading ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
          ) : bookmarkedEvents.length === 0 ? (
            <Card><CardContent className="py-10 text-center text-sm text-muted-foreground"><T k="auto.no-bookmarked-events-yet-0429" /></CardContent></Card>
          ) : (
            bookmarkedEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                isBookmarked={bookmarkedEventIds.includes(event.event_id)}
                onBookmarkToggle={() => {}}
                onSetAlert={() => {}}
                showBookmarkButton={!!user}
              />
            ))
          )}
        </TabsContent>
        <TabsContent value="account" className="mt-0">
          <VenueAccountPanel />
        </TabsContent>
      </Tabs>

      <ResearchCopilot />
    </PageShell>
  );
}
